// Copyright 2026 Mo-Sys Engineering Ltd. All Rights Reserved.
import { createSlice } from '@reduxjs/toolkit';
import { addAsyncCases, updateStateStatus } from '../utils/utils';
import {
  fetchCompanies,
  getCompanyById,
  fetchCompanyMembers,
  fetchCompanyMember,
  addUserToCompany,
  createCompany,
  updateCompany,
  updateCompanyOwner,
  removeCompanyUser,
  updateCompanyMemberRole,
  fetchCompanyInvites,
  createCompanyInvitation,
  revokeCompanyInvitation,
  createStudio,
  updateStudio,
  deleteStudio,
  fetchCompanyProviders,
  createProvider,
  updateProvider,
  deleteProvider,
  createKeyword,
  deleteKeyword,
  fetchCompanyEquipment,
  createCompanyEquipment,
  updateCompanyEquipment,
  fetchCompanyEquipmentTypes,
} from './companiesActions';

const initialState = {
  companies: [],
  company: {
    invites: [],
    studio: [],
    provider: [],
    keyword: [],
    members: [],
    company_equipment: [],
    company_equipment_types: [],
  },
  searchFilter: null, // e.g., { topic: 'members|invites|studios|providers|keywords',searchBy: 'query', query: 'test' }
  companyMember: null,
  status: {
    // possible status state : 'idle' | 'loading' | 'succeeded' | 'failed'
    companies: 'idle',
    company: 'idle',
  },
  error: {
    companies: null,
    company: null,
  },
};

const companiesSlice = createSlice({
  name: 'companies',
  initialState,
  reducers: {
    clearError: state => {
      state.error = initialState.error;
    },
    clearCompanyMember: state => {
      state.companyMember = null;
    },
    setSearchFilter(state, action) {
      state.searchFilter = action.payload;
    },
    clearSearchFilter(state) {
      state.searchFilter = null;
    },
    acceptedCompanyInvitation(state, action) {
      state.companies = [...state.companies, action.payload];
    },
  },
  extraReducers: builder => {
    addAsyncCases(builder, [
      { action: fetchCompanies, key: 'companies' },
      { action: getCompanyById, key: 'company' },
    ]);
    builder
      .addCase(fetchCompanyMember.fulfilled, (state, action) => {
        state.companyMember = action.payload;
        updateStateStatus(state, 'company', 'succeeded');
      })
      .addCase(fetchCompanyMember.rejected, (state, action) => {
        state.companyMember = null;
        updateStateStatus(state, 'company', 'failed', action);
      })
      .addCase(fetchCompanyMembers.fulfilled, (state, action) => {
        state.company.members = action.payload;
      })
      .addCase(fetchCompanyInvites.fulfilled, (state, action) => {
        // sort by pending invitation and descending expires date order
        state.company.invites = action.payload.slice().sort((a, b) => {
          // Pending first
          if (a.decision === null && b.decision !== null) {
            return -1;
          }
          if (a.decision !== null && b.decision === null) {
            return 1;
          }

          // expires descending
          const dateA = new Date(a.expires).getTime();
          const dateB = new Date(b.expires).getTime();
          return dateB - dateA;
        });
      })

      .addCase(createCompanyInvitation.fulfilled, (state, action) => {
        state.company.invites = [...state.company.invites, action.payload];
      })
      .addCase(createCompanyInvitation.rejected, (state, action) => {
        updateStateStatus(state, 'company', 'failed', action);
      })
      .addCase(revokeCompanyInvitation.fulfilled, (state, action) => {
        state.company.invites = state.company.invites.map(invite =>
          invite.token === action.payload?.token ? { ...invite, revoked: true } : invite
        );
        updateStateStatus(state, 'company', 'succeeded');
      })
      .addCase(revokeCompanyInvitation.rejected, (state, action) => {
        updateStateStatus(state, 'company', 'failed', action);
      })
      .addCase(createCompany.fulfilled, (state, action) => {
        state.companies = [...state.companies, action.payload];
        updateStateStatus(state, 'companies', 'succeeded');
      })
      .addCase(createCompany.rejected, (state, action) => {
        updateStateStatus(state, 'companies', 'failed', action);
      })
      .addCase(updateCompany.fulfilled, (state, action) => {
        state.company = { ...state.company, ...action.payload };
        state.companies = state.companies.map(c =>
          c.company_uuid === action.payload.company_uuid ? action.payload : c
        );
        updateStateStatus(state, 'company', 'succeeded');
      })
      .addCase(updateCompany.rejected, (state, action) => {
        updateStateStatus(state, 'company', 'failed', action);
      })

      .addCase(addUserToCompany.fulfilled, (state, action) => {
        state.companyMember = [...state.companyMember, action.payload];
        updateStateStatus(state, 'company', 'succeeded');
      })
      .addCase(addUserToCompany.rejected, (state, action) => {
        updateStateStatus(state, 'company', 'failed', action);
      })
      .addCase(updateCompanyOwner.fulfilled, (state, action) => {
        state.company = action.payload;
        updateStateStatus(state, 'company', 'succeeded');
      })
      .addCase(updateCompanyOwner.rejected, (state, action) => {
        updateStateStatus(state, 'company', 'failed', action);
      })
      .addCase(removeCompanyUser.fulfilled, (state, action) => {
        const userId = action.payload.user_id;
        state.company.members = state.company.members.filter(member => member.user_id !== userId);
        updateStateStatus(state, 'company', 'succeeded');
      })
      .addCase(removeCompanyUser.rejected, (state, action) => {
        updateStateStatus(state, 'company', 'failed', action);
      })
      .addCase(updateCompanyMemberRole.fulfilled, (state, action) => {
        state.companyMember = action.payload;
        state.company.members = state.company.members.map(member =>
          member.company_member_id === action.payload.company_member_id
            ? { ...member, company_role: action.payload.company_role }
            : member
        );
        updateStateStatus(state, 'company', 'succeeded');
      })
      .addCase(updateCompanyMemberRole.rejected, (state, action) => {
        updateStateStatus(state, 'company', 'failed', action);
      })
      // studio
      .addCase(createStudio.fulfilled, (state, action) => {
        state.company.studio = [...state.company.studio, action.payload];
        updateStateStatus(state, 'company', 'succeeded');
      })
      .addCase(createStudio.rejected, (state, action) => {
        updateStateStatus(state, 'company', 'failed', action);
      })
      .addCase(updateStudio.fulfilled, (state, action) => {
        state.company.studio = state.company.studio.map(s =>
          s.studio_uuid === action.payload.studio_uuid ? action.payload : s
        );
        updateStateStatus(state, 'company', 'succeeded');
      })
      .addCase(updateStudio.rejected, (state, action) => {
        updateStateStatus(state, 'company', 'failed', action);
      })
      .addCase(deleteStudio.fulfilled, (state, action) => {
        state.company.studio = state.company.studio.filter(
          s => s.studio_uuid !== action.payload?.studio_uuid
        );
        updateStateStatus(state, 'company', 'succeeded');
      })
      .addCase(deleteStudio.rejected, (state, action) => {
        updateStateStatus(state, 'company', 'failed', action);
      })
      // provider
      .addCase(fetchCompanyProviders.fulfilled, (state, action) => {
        state.company.provider = action.payload;
        updateStateStatus(state, 'company', 'succeeded');
      })
      .addCase(fetchCompanyProviders.rejected, (state, action) => {
        updateStateStatus(state, 'company', 'failed', action);
      })
      .addCase(createProvider.fulfilled, (state, action) => {
        state.company.provider = [...state.company.provider, action.payload];
        updateStateStatus(state, 'company', 'succeeded');
      })
      .addCase(createProvider.rejected, (state, action) => {
        updateStateStatus(state, 'company', 'failed', action);
      })
      .addCase(updateProvider.fulfilled, (state, action) => {
        state.company.provider = state.company.provider.map(provider =>
          provider.provider_id === action.payload.provider_id ? action.payload : provider
        );
        updateStateStatus(state, 'company', 'succeeded');
      })
      .addCase(updateProvider.rejected, (state, action) => {
        updateStateStatus(state, 'company', 'failed', action);
      })
      .addCase(deleteProvider.fulfilled, (state, action) => {
        state.company.provider = state.company.provider.filter(
          p => p.provider_id !== action.payload
        );
        updateStateStatus(state, 'company', 'succeeded');
      })
      .addCase(deleteProvider.rejected, (state, action) => {
        updateStateStatus(state, 'company', 'failed', action);
      })
      // keywords
      .addCase(createKeyword.fulfilled, (state, action) => {
        state.company.keyword = [...state.company.keyword, action.payload];
        updateStateStatus(state, 'company', 'succeeded');
      })
      .addCase(createKeyword.rejected, (state, action) => {
        updateStateStatus(state, 'company', 'failed', action);
      })
      .addCase(deleteKeyword.fulfilled, (state, action) => {
        state.company.keyword = state.company.keyword.filter(k => k.keyword_id !== action.payload);
        updateStateStatus(state, 'company', 'succeeded');
      })
      .addCase(deleteKeyword.rejected, (state, action) => {
        updateStateStatus(state, 'company', 'failed', action);
      })
      // equipment
      .addCase(fetchCompanyEquipment.fulfilled, (state, action) => {
        state.company.company_equipment = action.payload;
        updateStateStatus(state, 'company', 'succeeded');
      })
      .addCase(createCompanyEquipment.fulfilled, (state, action) => {
        state.company.company_equipment = [...state.company.company_equipment, action.payload];
        updateStateStatus(state, 'company', 'succeeded');
      })
      .addCase(createCompanyEquipment.rejected, (state, action) => {
        updateStateStatus(state, 'company', 'failed', action);
      })
      .addCase(updateCompanyEquipment.fulfilled, (state, action) => {
        state.company.company_equipment = state.company.company_equipment.map(equip =>
          equip.co_equip_uuid === action.payload.co_equip_uuid
            ? { ...equip, ...action.payload }
            : equip
        );
        updateStateStatus(state, 'company', 'succeeded');
      })
      .addCase(updateCompanyEquipment.rejected, (state, action) => {
        updateStateStatus(state, 'company', 'failed', action);
      })
      // equipment types
      .addCase(fetchCompanyEquipmentTypes.fulfilled, (state, action) => {
        state.company.company_equipment_types = action.payload;
        updateStateStatus(state, 'company', 'succeeded');
      })
      .addCase(fetchCompanyEquipmentTypes.rejected, (state, action) => {
        updateStateStatus(state, 'company', 'failed', action);
      });
  },
});

export const {
  clearError,
  clearCompanyMember,
  setSearchFilter,
  clearSearchFilter,
  acceptedCompanyInvitation,
} = companiesSlice.actions;

export const companiesStatus = state => state.companies.status;
export const companiesError = state => state.companies.error;

export default companiesSlice.reducer;
